/**
 * Screen snapshot of a detail record (plan 1968 F13): the fields on the tab the user is
 * looking at — label, SAVED value formatted for display, section label. Doctype-agnostic:
 * driven by the page's tab structure (DetailPageTabsContext) + meta flags.
 *
 * Never: unsaved edits (pass the saved doc), other tabs, hidden / depends_on-hidden
 * fields, secret-looking fields, layout / media / code fields, empty values.
 */

import type { TabStructure } from "@/features/inventory/item-detail/utils/buildTabsFromMeta";
import { formatShopifyFieldValue } from "@/features/inventory/item-detail/components/utils/shopifyFormatting";
import { evaluateDependsOn } from "@/lib/erp/meta/utils/dependsOnEvaluator";
import { SECRET_FIELDNAME, SKIPPED_FIELDTYPES } from "@/lib/erp/recordSummary";
import type { DocFieldSchema } from "@/lib/erp/meta";
import { oneLine } from "./screenDom";
import { PAGE_CONTEXT_LIMITS, type ScreenField, type ScreenSectionInput } from "./types";

export const DETAIL_SCREEN_SECTION_ID = "detail";

/** On screen as text even though record summaries skip them. */
const SHOWN_ON_SCREEN = new Set(["Table", "Table MultiSelect", "Text Editor"]);

type Doc = Record<string, unknown>;

/** The tab the page renders: the active one when it exists, else the first (TabRenderer rule). */
export function resolveVisibleTab(tabs: TabStructure[], activeTab: string): TabStructure | null {
  if (!tabs.length) return null;
  if (activeTab) return tabs.find((t) => t.name === activeTab) ?? null;
  return tabs[0];
}

function htmlToText(html: string): string {
  if (typeof DOMParser === "undefined") return html.replace(/<[^>]+>/g, " ");
  return new DOMParser().parseFromString(html.replace(/<br\s*\/?>/gi, "\n"), "text/html").body.textContent ?? "";
}

function displayValue(field: DocFieldSchema, doc: Doc): string {
  const raw = doc[field.fieldname];
  if (raw === null || raw === undefined || raw === "") return "";
  if (Array.isArray(raw) && raw.length === 0) return "";
  const currency = typeof doc.currency === "string" ? doc.currency : undefined;
  const formatted = formatShopifyFieldValue(field, raw, currency);
  return oneLine(typeof formatted === "string" ? formatted : htmlToText(formatted.__html));
}

function isShown(field: DocFieldSchema, doc: Doc): boolean {
  if (field.hidden === 1) return false;
  if (SECRET_FIELDNAME.test(field.fieldname)) return false;
  if (SKIPPED_FIELDTYPES.has(field.fieldtype) && !SHOWN_ON_SCREEN.has(field.fieldtype)) return false;
  return evaluateDependsOn(field.depends_on, doc, undefined, "visibility");
}

function* tabFields(tab: TabStructure): Generator<{ field: DocFieldSchema; group: string }> {
  for (const section of tab.sections) {
    const group = oneLine(section.label || "");
    for (const column of section.columns ?? []) {
      for (const field of column.fields ?? []) yield { field, group };
    }
  }
}

export function buildDetailScreenSection(
  tabs: TabStructure[],
  activeTab: string,
  savedDoc: Doc | null | undefined,
): ScreenSectionInput | null {
  const tab = savedDoc ? resolveVisibleTab(tabs, activeTab) : null;
  if (!tab || !savedDoc) return null;
  const fields: ScreenField[] = [];
  for (const { field, group } of tabFields(tab)) {
    if (fields.length >= PAGE_CONTEXT_LIMITS.maxScreenFields) break;
    const value = isShown(field, savedDoc) ? displayValue(field, savedDoc) : "";
    if (value) fields.push({ label: oneLine(field.label || field.fieldname), value, ...(group ? { group } : {}) });
  }
  if (!fields.length) return null;
  return { ...(tab.label ? { title: oneLine(tab.label) } : {}), fields };
}
